import React from "react";
import { Button } from "@/components/ui/button";
import { FilterX } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The panel's controls row: optional title, the AND/OR match toggle, and
 * Clear-all.
 *
 * Split out of `AdvancedFilterPanel.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/AdvancedFilterPanel*.test.tsx`.
 *
 * Rendered in BOTH modes on purpose: the operator toggle and Clear-all used to
 * disappear along with the header when the panel was embedded in a popover.
 */
export function FilterPanelControls({
  hideHeader,
  filterOperator,
  setFilterOperator,
  clearAllAdvancedFilters,
  hasActiveFilters,
}: {
  hideHeader?: boolean;
  filterOperator: "AND" | "OR";
  setFilterOperator: (operator: "AND" | "OR") => void;
  clearAllAdvancedFilters: () => void;
  hasActiveFilters: boolean;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      {!hideHeader && <h3 className="text-sm font-semibold text-foreground">Advanced Filters</h3>}
      <div className="ml-auto flex items-center gap-2">
        <div
          role="group"
          aria-label="Match mode"
          className="inline-flex items-center rounded-md border border-border bg-muted/50 p-0.5"
        >
          {(["AND", "OR"] as const).map((op) => {
            const active = filterOperator === op;
            return (
              <button
                key={op}
                type="button"
                aria-pressed={active}
                onClick={() => setFilterOperator(op)}
                className={cn(
                  "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors",
                  active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {op === "AND" ? "Match all" : "Match any"}
              </button>
            );
          })}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={clearAllAdvancedFilters}
          disabled={!hasActiveFilters}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
        >
          <FilterX className="h-3.5 w-3.5" />
          <span className="ml-1">Clear all</span>
        </Button>
      </div>
    </div>
  );
}
